import React from 'react';
import { useDashboard } from '@/api';
import { useBranchScope } from '@/context/BranchScopeContext';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@/components/ui';
import { LoadingSpinner } from '@/components/common';
import { formatAttendanceTime } from '../utils/datetime';

export default function DashboardPage() {
  const { branchParam } = useBranchScope();
  const { data: dashboard, isLoading } = useDashboard({ branch_id: branchParam });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const stats = dashboard?.data || {};
  const today = stats.today || {};
  const thisWeek = stats.this_week || {};
  const thisMonth = stats.this_month || {};
  const deviceStatus = stats.device_status || {};

  return (
    <div className="page-shell space-y-6">
      {/* Header */}
      <div className="page-toolbar">
        <div>
          <h1 className="text-3xl font-bold">Панель управления</h1>
          <p className="text-gray-500">{new Date().toLocaleDateString('ru-RU')}</p>
        </div>
      </div>

      {/* Today's Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">
              Ожидаемые приходы
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold sm:text-4xl">{today.expected_arrivals || 0}</div>
            <p className="text-xs text-gray-500 mt-2">сотрудников</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">
              Пришли
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600 sm:text-4xl">
              {today.arrived_count || 0}
            </div>
            <p className="text-xs text-gray-500 mt-2">отметились</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">
              Опоздали
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600 sm:text-4xl">
              {today.late_count || 0}
            </div>
            <p className="text-xs text-gray-500 mt-2">сотрудников</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">
              Не вышли
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600 sm:text-4xl">
              {today.no_show_count || 0}
            </div>
            <p className="text-xs text-gray-500 mt-2">сотрудников</p>
          </CardContent>
        </Card>
      </div>

      {/* Late List */}
      {today.late_list?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Опоздавшие</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto ui-table-wrap">
              <table className="w-full text-sm ui-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-4">Сотрудник</th>
                    <th className="text-left py-2 px-4">Минуты опоздания</th>
                    <th className="text-left py-2 px-4">Время прихода</th>
                  </tr>
                </thead>
                <tbody>
                  {today.late_list.map((row: any) => (
                    <tr key={row.employee_id} className="border-t">
                      <td className="py-3 px-4">{row.name}</td>
                      <td className="py-3 px-4">
                        <Badge variant="warning">{row.late_minutes} мин</Badge>
                      </td>
                      <td className="py-3 px-4">
                        {formatAttendanceTime(row.first_in_at, 'ru-RU') ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Absence Streaks */}
      {stats.absence_streaks?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Длительные отсутствия</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.absence_streaks.map((streak: any, idx: number) => (
                <div key={idx} className="flex flex-col gap-3 rounded-xl bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">{streak.employee_name}</p>
                    <p className="text-sm text-gray-500">
                      Дата начала: {new Date(streak.start_date).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                  <Badge variant="destructive">
                    {streak.consecutive_days} дн.
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Device Status */}
      <Card>
        <CardHeader>
          <CardTitle>Состояние устройств</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {deviceStatus.active || 0}
              </div>
              <p className="text-sm text-gray-500">Активные</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {deviceStatus.maintenance || 0}
              </div>
              <p className="text-sm text-gray-500">На обслуживании</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {deviceStatus.offline || 0}
              </div>
              <p className="text-sm text-gray-500">Офлайн</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Week Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Недельный отчёт</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <div>
              <p className="text-sm text-gray-600">Отработанные дни</p>
              <p className="text-2xl font-bold">{thisWeek.worked_days || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Дни опозданий</p>
              <p className="text-2xl font-bold text-orange-600">{thisWeek.late_count || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Дни неявки</p>
              <p className="text-2xl font-bold text-red-600">{thisWeek.no_show_count || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Сверхурочная работа</p>
              <p className="text-2xl font-bold">{thisWeek.overtime_hours || 0} ч</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

